import type { TextFieldProps as MuiTextFieldProps } from '@mui/material/TextField';

import { useFormContext, useController } from 'react-hook-form';
import getUnicodeFlagIcon from 'country-flag-icons/unicode';
import { ReadOnlyField, qaProp } from '@mfw/components';
import FormControl from '@mui/material/FormControl';
import { FormCheckboxField } from '@mfw/forms';
import { useIntl } from 'react-intl';
import classNames from 'classnames';
import React from 'react';

import type { PhoneCountryCodeValue } from './PhoneCountryCode';
import type { BaseFieldType } from './types';

import { DEFAULT_COUNTRY } from '../../schemas/address.schema';
import TextFieldSkeleton from '../TextFieldSkeleton';
import { countries } from '../../config/countries';
import PhoneCountryCode from './PhoneCountryCode';
import MenuItem from '../Menu/MenuItem';
import TextField from './TextField';
import messages from './messages';
import './PhoneNumber.scss';

export type PhoneNumberProps = {
  phoneCountryCodeLabel?: string;
  phoneCountryCodeName: string;
  phoneNumberPublic: string;
  phoneNumberName: string;
  'data-testid'?: string;
  readOnly?: boolean;
  qa?: string;
} & Omit<MuiTextFieldProps, 'name'> &
  Omit<BaseFieldType, 'name'>;

const PhoneNumber = ({
  className,
  'data-testid': testId,
  isLoading,
  label,
  phoneCountryCodeLabel,
  phoneCountryCodeName,
  phoneNumberName,
  phoneNumberPublic,
  qa,
  readOnly,
  required,
  ...otherProps
}: PhoneNumberProps) => {
  const { formatMessage } = useIntl();
  const { control, getValues, setValue } = useFormContext();
  const {
    fieldState: { error: phoneNumberError },
  } = useController({
    control,
    name: phoneNumberName,
  });

  const phoneNumberValue = getValues(phoneNumberName);
  const phoneCountryCode = getValues(phoneCountryCodeName);

  // TODO Maybe preselected on locale or user location
  const [selectedCountry, setSelectedCountry] =
    React.useState<PhoneCountryCodeValue>({
      countryCode: DEFAULT_COUNTRY.value,
      dialCode: DEFAULT_COUNTRY.dialCode,
      flag: getUnicodeFlagIcon(DEFAULT_COUNTRY.value),
    });
  React.useEffect(() => {
    setValue(phoneCountryCodeName, selectedCountry.countryCode);
  }, [phoneCountryCodeName, selectedCountry.countryCode, setValue]);

  const mappedCountries = React.useMemo(() => {
    return countries.map((country) => {
      return {
        countryCode: country.countryCode,
        dialCode: `+${country.dialCode}`,
        flag: getUnicodeFlagIcon(country.countryCode),
      };
    });
  }, []);

  const handleCountryCodeChange = React.useCallback(
    (value: PhoneCountryCodeValue) => {
      setSelectedCountry(value);
      setValue(phoneCountryCodeName, value.countryCode, {
        shouldValidate: true,
      });
    },
    [phoneCountryCodeName, setValue, setSelectedCountry],
  );
  const fieldHasErrors = React.useMemo(
    () => Boolean(phoneNumberError),
    [phoneNumberError],
  );

  React.useEffect(() => {
    if (!phoneCountryCode) return;
    const country = mappedCountries.find(
      (country) => country.countryCode === phoneCountryCode,
    );
    if (!country) return;
    setSelectedCountry(country);
  }, [phoneCountryCodeName, mappedCountries, phoneCountryCode]);

  const memoizedList = React.useMemo(() => {
    return mappedCountries.map((country) => {
      return (
        <MenuItem
          onClick={() => handleCountryCodeChange(country)}
          key={country.countryCode}
        >
          <div>
            <span
              {...qaProp(qa, '__flag')}
              className="Mfw-PhoneNumber-countryCode__flag"
            >
              {country.flag}
            </span>
            <span
              {...qaProp(qa, '__dialCode')}
              className="Mfw-PhoneNumber-countryCode__dialCode"
            >
              {country.dialCode}
            </span>
          </div>
        </MenuItem>
      );
    });
  }, [handleCountryCodeChange, mappedCountries, qa]);
  return (
    <div className={classNames('Mfw-PhoneNumber-root', className)}>
      {isLoading && <TextFieldSkeleton />}
      {readOnly && (
        <ReadOnlyField
          value={`${selectedCountry?.flag} ${selectedCountry?.dialCode} ${phoneNumberValue}`}
          label={label || formatMessage(messages.phoneNumberLabel)}
        />
      )}
      {!isLoading && !readOnly && (
        <FormControl
          className="Mfw-PhoneNumber__form-control"
          component="fieldset"
          fullWidth
        >
          <PhoneCountryCode
            data-testid={testId ? `${testId}-country-code` : undefined}
            className={'Mfw-PhoneNumber-countryCode'}
            error={Boolean(fieldHasErrors)}
            defaultValue={selectedCountry}
            placement="bottom-start"
            id="my-input"
            asList
          >
            {memoizedList}
          </PhoneCountryCode>
          <TextField
            className={classNames('Mfw-PhoneNumber-textField', {
              'Mfw-PhoneNumber-textField--has-value': Boolean(phoneNumberValue),
            })}
            label={label || formatMessage(messages.phoneNumberLabel)}
            {...otherProps}
            name={phoneNumberName}
            inputProps={{ inputMode: 'tel' }}
            data-testid={testId}
            placeholder=""
            // type="number" rejected "+", spaces and leading zeros — a phone
            // number is not a quantity
            type="tel"
          />
          {!readOnly && (
            <FormCheckboxField
              helperText={formatMessage(messages.isPhoneNumberPublicHelperText)}
              data-testid={testId ? `${testId}-public` : undefined}
              label={formatMessage(messages.isPhoneNumberPublic)}
              name={phoneNumberPublic}
            />
          )}
        </FormControl>
      )}
    </div>
  );
};

export default PhoneNumber;
